import { useEffect, useRef, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Alert } from '../components'
import {
  getUploadLinkStatus,
  listUploadLinkDocuments,
  uploadDocumentViaLink,
  UploadLinkStatus,
} from '../services/uploadLinkService'
import { MatterDocument } from '../services/documentService'

interface DocumentUploadPageProps {
  token: string
}

type PageState = 'loading' | 'invalid' | 'ready'

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const DocumentUploadPage: React.FC<DocumentUploadPageProps> = ({ token }) => {
  const [pageState, setPageState] = useState<PageState>('loading')
  const [invalidReason, setInvalidReason] = useState('')
  const [linkStatus, setLinkStatus] = useState<UploadLinkStatus | null>(null)
  const [documents, setDocuments] = useState<MatterDocument[]>([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadDocuments = async () => {
    setDocumentsLoading(true)
    try {
      const results = await listUploadLinkDocuments(token)
      setDocuments(results)
    } catch (err) {
      // Non-fatal — the upload form itself still works even if the list fails.
      console.error('Failed to load documents:', err)
    } finally {
      setDocumentsLoading(false)
    }
  }

  useEffect(() => {
    getUploadLinkStatus(token)
      .then((status) => {
        setLinkStatus(status)
        setPageState('ready')
        loadDocuments()
      })
      .catch((err) => {
        setInvalidReason(err instanceof Error ? err.message : 'This link is not valid')
        setPageState('invalid')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setError(null)
    setUploading(true)
    try {
      const document = await uploadDocumentViaLink(token, file)
      setDocuments((prev) => [document, ...prev])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
          {linkStatus && (
            <p className="text-sm text-gray-600 mt-1">
              {linkStatus.matterType} &middot; {linkStatus.propertyAddress}
            </p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Your Files</CardTitle>
          </CardHeader>
          <CardContent>
            {pageState === 'loading' && <p className="text-gray-600">Loading...</p>}

            {pageState === 'invalid' && <Alert type="error">{invalidReason}</Alert>}

            {pageState === 'ready' && (
              <div className="space-y-4">
                {error && <Alert type="error">{error}</Alert>}

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelected}
                />
                <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Choose File to Upload'}
                </Button>

                <div className="pt-4 border-t border-gray-100">
                  {documentsLoading ? (
                    <p className="text-sm text-gray-500">Loading files...</p>
                  ) : documents.length === 0 ? (
                    <p className="text-sm text-gray-500">No files uploaded yet</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {documents.map((doc) => (
                        <li key={doc.id} className="py-2.5">
                          <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.uploadedBy === 'client' ? 'Uploaded by you' : 'Uploaded by your conveyancer'} · {formatDate(doc.createdAt)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
